import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Empty, Progress, Row, Table, Typography } from 'antd'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { FailureReason, getPaymentMetrics, Granularity, MetricsPeriod } from '../../../Actions/operationsMetricsApi'
import StatCard from './StatCard'
import { formatCompact, formatCurrency, formatNumber } from './formatters'
import { StatusTags } from './statusMeta'

const BAR_COLORS = ['#1677ff', '#52c41a', '#faad14', '#eb2f96']

const failureColumns = [
  {
    title: '실패 사유 (PG 응답)',
    dataIndex: 'message',
    key: 'message',
    render: (message: string | null) => message ?? '(사유 없음)',
  },
  {
    title: '건수',
    dataIndex: 'count',
    key: 'count',
    align: 'right' as const,
    width: 80,
    render: (count: number) => formatNumber(count),
  },
]

interface Props {
  period: MetricsPeriod
  granularity: Granularity
}

/** 💳 결제 — 결제액 추이(통화별 시리즈 분리), 성공률, 상태별 건수, 실패사유 Top5 */
export default function PaymentsSection({ period, granularity }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'payments', period, granularity],
    queryFn: () => getPaymentMetrics({ ...period, granularity }),
  })

  // trend(bucket × 통화) → 차트용 피벗: [{ bucket, KRW: 12000, USD: 30 }]
  const { chartData, currencies } = useMemo(() => {
    const trend = data?.trend ?? []
    // DB에 통화가 저장되지 않은 레거시 데이터는 전부 원화로 간주
    const currencySet = Array.from(new Set(trend.map((t) => t.currencyCode || 'KRW'))).sort()
    const byBucket = new Map<string, Record<string, number | string>>()
    trend.forEach((t) => {
      const row = byBucket.get(t.bucket) ?? { bucket: t.bucket }
      row[t.currencyCode || 'KRW'] = t.paidAmount
      byBucket.set(t.bucket, row)
    })
    return {
      chartData: Array.from(byBucket.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket))),
      currencies: currencySet,
    }
  }, [data])

  if (error) return <Alert type="error" message="결제 지표를 불러오지 못했습니다." description={(error as Error).message} />

  const successPercent = data?.successRate != null ? Number((data.successRate * 100).toFixed(1)) : null

  return (
    <Card title="💳 결제" size="small">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={16}>
          <Typography.Text strong>결제액 추이 (paid 기준 · 통화별 분리)</Typography.Text>
          {chartData.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="기간 내 결제 없음" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" fontSize={12} />
                <YAxis tickFormatter={(v) => formatCompact(Number(v))} fontSize={12} />
                <Tooltip
                  formatter={(value, name) => [formatCurrency(String(name), Number(value)), String(name)]}
                />
                <Legend />
                {currencies.map((currency, index) => (
                  <Bar key={currency} dataKey={currency} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Col>
        <Col xs={24} md={8}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Card size="small" loading={isLoading}>
                <Typography.Text type="secondary">결제 성공률 (paid / paid+failed)</Typography.Text>
                <div style={{ marginTop: 8 }}>
                  {successPercent == null ? (
                    <Typography.Text>— (기간 내 결제 시도 없음)</Typography.Text>
                  ) : (
                    <Progress
                      percent={successPercent}
                      status={successPercent < 90 ? 'exception' : 'normal'}
                      format={(p) => `${p}%`}
                    />
                  )}
                </div>
              </Card>
            </Col>
            {(data?.totals ?? []).map((total) => (
              <Col span={24} key={total.currencyCode || 'KRW'}>
                <StatCard
                  title={`기간 내 결제액 (${total.currencyCode || 'KRW'} · ${formatNumber(total.paidCount)}건)`}
                  value={formatCurrency(total.currencyCode, total.paidAmount)}
                  loading={isLoading}
                />
              </Col>
            ))}
            <Col span={24}>
              <Card size="small" loading={isLoading}>
                <Typography.Text type="secondary">인보이스 상태별 건수</Typography.Text>
                <div style={{ marginTop: 8 }}>
                  <StatusTags statusCounts={data?.statusCounts ?? {}} />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      {(data?.topFailureReasons?.length ?? 0) > 0 && (
        <Row style={{ marginTop: 12 }}>
          <Col xs={24} md={16}>
            <Typography.Text strong>실패 사유 Top 5</Typography.Text>
            <Table
              rowKey={(row: FailureReason) => row.message ?? '(none)'}
              columns={failureColumns}
              dataSource={data?.topFailureReasons ?? []}
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
            />
          </Col>
        </Row>
      )}
    </Card>
  )
}